import { Skeleton } from '@agency/ui';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, type ReactNode } from 'react';
import { useSession } from '../lib/session.js';

/** Redirects to /login (with returnTo) when the session is missing; shows a skeleton while loading. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const session = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const anonymous = session.status === 'anonymous';
  const redirected = useRef(false);

  useEffect(() => {
    if (!anonymous) {
      redirected.current = false;
      return;
    }
    // The layout stays mounted while the navigation is pending; redirect exactly once.
    if (redirected.current || location.pathname === '/login') return;
    redirected.current = true;
    const returnTo = `${location.pathname}${location.searchStr}`;
    void navigate({ to: '/login', search: { returnTo: returnTo === '/' ? undefined : returnTo }, replace: true });
  }, [anonymous, navigate, location.pathname, location.searchStr]);

  if (session.status !== 'authenticated') {
    return (
      <div className="flex h-screen w-screen bg-bg" aria-busy="true" aria-label={anonymous ? 'Redirecting to sign in' : 'Loading session'}>
        <div className="w-[248px] border-r border-line bg-panel" />
        <div className="flex-1 p-8">
          <Skeleton width={320} height={28} />
          <Skeleton width={480} height={14} className="mt-3" />
        </div>
      </div>
    );
  }
  return <>{children}</>;
}
